import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { graphql, readInlineData } from "react-relay";

import { BaseLink } from "shared/components/common/base-link";

import { EntityLink } from "scmp-app/components/entity-link";
import { getResponsiveProps } from "scmp-app/components/header/features/helpers";
import type { HeaderFeatureCommonProps } from "scmp-app/components/header/features/types";
import { useMastheadTracking } from "scmp-app/components/header/hooks";
import { currentArticleAtom } from "scmp-app/lib/current-item";
import { pathname as videoHomepagePathname } from "scmp-app/pages/video";
import type { entityLinkHeaderFeaturesArticle$key } from "scmp-app/queries/__generated__/entityLinkHeaderFeaturesArticle.graphql";

import { Container } from "./styles";

export type entityLinkNameSize = "large" | "small";

export type HeaderFeaturesEntityLinkProps = HeaderFeatureCommonProps & {
  isMagazineStyle?: boolean;
  size?: entityLinkNameSize;
  variant?: "center" | "left";
};

export const HeaderFeaturesEntityLink: FunctionComponent<HeaderFeaturesEntityLinkProps> = ({
  isMagazineStyle = false,
  size = "small",
  variant = "left",
  ...props
}) => {
  const currentArticle = useAtomValue(currentArticleAtom);
  const mastheadQueryParameter = useMastheadTracking();
  if (!currentArticle) return null;

  if (currentArticle.__typename === "Video") {
    return (
      <Container $isMagazineStyle={isMagazineStyle} $size={size} {...getResponsiveProps(props)}>
        <BaseLink pathname={videoHomepagePathname} query={mastheadQueryParameter}>
          Video
        </BaseLink>
      </Container>
    );
  }

  const data = readInlineData<entityLinkHeaderFeaturesArticle$key>(
    graphql`
      fragment entityLinkHeaderFeaturesArticle on Article @inline {
        authors {
          name
          ...entityLink
        }
        sectionsV2 {
          value {
            ... on Section {
              name
              published
              ...entityLink
            }
          }
        }
      }
    `,
    currentArticle,
  );

  const sectionPath = data?.sectionsV2?.[0]?.value;
  if (!sectionPath || sectionPath.length === 0) return null;

  const rootSection = sectionPath[0];
  const targetSection = sectionPath.toReversed().find(section => section?.published === true);

  if (!targetSection || !rootSection) return null;

  const rootName = rootSection.name;
  const targetName = targetSection.name;

  const editorialAuthor = data.authors?.find(
    author => author?.name?.trim().toLowerCase() === "scmp editorial",
  );
  const isSCMPEditorial = !!editorialAuthor;
  const isOpinionRoot = rootName === "Opinion";

  let displayReference: typeof editorialAuthor | typeof targetSection = targetSection;
  let displayName = targetName ?? null;

  if (isOpinionRoot) {
    displayReference = rootSection;
    displayName = rootName ?? null;

    if (variant === "center" && isSCMPEditorial) {
      displayName = "Editorial";
      displayReference = editorialAuthor;
    }
  }

  return (
    <Container $isMagazineStyle={isMagazineStyle} $size={size} {...getResponsiveProps(props)}>
      <EntityLink query={mastheadQueryParameter} reference={displayReference}>
        {displayName}
      </EntityLink>
    </Container>
  );
};

HeaderFeaturesEntityLink.displayName = "HeaderFeaturesEntityLink";
